import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

// Cookie-only authentication (Feb 2026 migration) + CSRF double-submit protection.
// - The JWT lives ONLY in an HttpOnly cookie set by the backend on /auth/login,
//   /auth/mfa/verify, and /auth/mfa/enable.
// - No JavaScript can read the access_token — this eliminates the XSS token-theft
//   surface flagged by the code review.
// - We determine login state by calling /auth/me on mount. If the cookie is
//   present + valid we get 200 + user; otherwise 401 → we show the login screen.
// - `data.access_token` returned by the login response is IGNORED here (the
//   cookie is already set); we only use `data.user`.
// - On app boot we also request GET /auth/csrf so the JS-readable XSRF-TOKEN
//   cookie is set. Axios auto-copies it to the X-CSRF-Token header on every
//   state-changing request. Without that header the CSRF middleware rejects
//   the request with 403.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out, object = logged in
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);

  const bootstrapCsrf = async () => {
    // Best-effort — an already-set XSRF-TOKEN cookie will be reused server-side.
    try { await api.get("/auth/csrf"); } catch { /* ignore */ }
  };

  const reloadUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      const { session_expires_at, ...u } = data;
      setUser(u);
      setSessionExpiresAt(session_expires_at || null);
      return u;
    } catch (e) {
      setUser(false);
      setSessionExpiresAt(null);
      return null;
    }
  };

  useEffect(() => {
    (async () => { await bootstrapCsrf(); await reloadUser(); })();
  }, []);

  const login = async (email, password, context = null) => {
    // Ensure the CSRF cookie is set before the login POST so the browser can
    // echo it into X-CSRF-Token. login itself is on the exempt allow-list, but
    // we still want the cookie present for the very next authenticated call.
    await bootstrapCsrf();
    const body = context ? { email, password, context } : { email, password };
    const { data } = await api.post("/auth/login", body);
    return data;
  };

  const completeAuth = (data) => {
    // Cookie is already set by the backend response. Nothing to persist locally.
    setUser(data.user);
    // The response may not include the expiry; /auth/me will fetch it next.
    reloadUser();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* server-side logout best-effort */ }
    setUser(false);
    setSessionExpiresAt(null);
  };

  const refreshSession = async () => {
    try {
      const { data } = await api.post("/auth/refresh");
      setSessionExpiresAt(data.session_expires_at || null);
      return true;
    } catch { return false; }
  };

  return (
    <AuthContext.Provider value={{ user, sessionExpiresAt, login, completeAuth, logout, reloadUser, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
